import { useEffect, useState } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem,
  Button, Alert, Typography, Chip,
} from "@mui/material";
import { adminApi } from "rsa/adminApi";
import SectionHeader from "../Components/SectionHeader";
import StatusChip from "../Components/StatusChip";

const cardSx = { borderRadius: "15px", boxShadow: "rgba(0,0,0,0.1) 0px 10px 15px -3px, rgba(0,0,0,0.05) 0px 4px 6px -2px", p: 2 };

const HEALTH_LABEL = { maps: "Maps / tiles", sms: "SMS / OTP", push: "Push", payments: "Payments", whatsapp: "WhatsApp fallback" };

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [status, setStatus] = useState("failed");
  const [health, setHealth] = useState(null);
  const [error, setError] = useState("");
  const [seedMsg, setSeedMsg] = useState("");

  const load = async () => {
    try {
      setError("");
      const [p, h] = await Promise.all([
        adminApi("GET", `/admin/payments${status ? `?status=${status}` : ""}`),
        adminApi("GET", "/admin/system/health"),
      ]);
      setPayments(p); setHealth(h);
    } catch (e) { setError(e.status === 401 ? "Session expired — sign in again." : e.message); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const resolve = async (p, newStatus) => {
    await adminApi("PATCH", `/admin/payments/${p.id}`, { status: newStatus });
    load();
  };

  const seedHere = () => {
    setSeedMsg("Locating…");
    const done = async (lat, lng) => {
      try {
        const r = await adminApi("POST", "/admin/demo/seed-vendors", { lat, lng, count: 6, radiusKm: 8 });
        setSeedMsg(`Seeded ${r.seeded} vendors near you: ${r.vendors.join(", ")}`);
      } catch (e) { setSeedMsg("Error: " + e.message); }
    };
    if (!navigator.geolocation) return done(19.076, 72.8777);
    navigator.geolocation.getCurrentPosition(
      (p) => done(p.coords.latitude, p.coords.longitude),
      () => done(19.076, 72.8777), { enableHighAccuracy: true, timeout: 8000 });
  };

  return (
    <Box>
      <SectionHeader title="Payments & System Health" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <Card sx={{ ...cardSx, flex: "2 1 480px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>Payment issues</Typography>
            <Select size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="created">Created (stuck)</MenuItem>
              <MenuItem value="authorized">Authorized</MenuItem>
              <MenuItem value="captured">Captured</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Request</TableCell><TableCell>Customer</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell><TableCell>Action</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 && <TableRow><TableCell colSpan={5} align="center">No payments in this state</TableCell></TableRow>}
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.request_ref}</TableCell>
                  <TableCell>{p.customer_name}</TableCell>
                  <TableCell>₹{Math.round(p.amount)}</TableCell>
                  <TableCell><StatusChip value={p.status} /></TableCell>
                  <TableCell>
                    {p.status === "failed" && <Button size="small" onClick={() => resolve(p, "refunded")}>Mark refunded</Button>}
                    {p.status === "captured" && <Button size="small" color="warning" onClick={() => resolve(p, "refunded")}>Refund</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card sx={{ ...cardSx, flex: "1 1 260px" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Adapter health</Typography>
          {!health && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
          {health && Object.entries(HEALTH_LABEL).map(([key, label]) => (
            <Box key={key} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2">{label}</Typography>
              <Chip size="small" label={health[key]} color={health[key] === "live" ? "success" : "warning"} />
            </Box>
          ))}
        </Card>
      </Box>

      <Card sx={{ ...cardSx, opacity: 0.75, border: "1px dashed #ccc" }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Rehearsal tool — not a real ops action.
        </Typography>
        <Button size="small" variant="outlined" onClick={seedHere}>📍 Seed vendors near me</Button>
        {seedMsg && <Typography variant="caption" display="block" mt={1}>{seedMsg}</Typography>}
      </Card>
    </Box>
  );
}
